/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { NgClass } from "@angular/common";
import { Component, OnDestroy, OnInit, signal, TemplateRef, ViewChild } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { GridApi, GridOptions, GridReadyEvent, RowSelectedEvent } from "ag-grid-community";
import { EChartsOption } from "echarts";
import { forkJoin, last, map, repeat, switchMap, takeWhile, tap, throwError } from "rxjs";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { CheckboxComponent } from "../../../shared/components/checkbox/checkbox.component";
import { ChipsComponent } from "../../../shared/components/chips/chips.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { UploadedFileStatusComponent } from "../../../shared/components/table/cell-renderers/uploaded-file-status/uploaded-file-status.component";
import { TableComponent } from "../../../shared/components/table/table.component";
import { UploadFileComponent } from "../../../shared/components/upload-file/upload-file.component";
import { BarChart } from "../../../shared/models/http/charts/bar-chart/bar-chart.model";
import { HardwareAssetsFile } from "../../../shared/models/http/dashboard/hardware-assets-file.model";
import { ApplicationError } from "../../../shared/models/ui/application-error.model";
import { ChartResponse } from "../../../shared/models/ui/charts.model";
import { ChipOption } from "../../../shared/models/ui/chip-option.model";
import { EmissionViewMode } from "../../../shared/models/ui/emission-view-mode.model";
import { HardwareAssetsScopeOrderBy } from "../../../shared/models/ui/hardware-assets-scope-orderby.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { DashboardHardwareAssetsService } from "../../../shared/services/dashboard-hardware-assets.service";
import { DialogService } from "../../../shared/services/dialog.service";
import { StorageService } from "../../../shared/services/storage.service";
import { COLORS, EMISSION_TYPE_OPTIONS, ORDER_BY_OPTIONS } from "../../../shared/utils/constants";
import { CARBON_FOOTPRINT_BAR_CHART_OVERRIDES } from "../../../shared/utils/echarts/chart-constants";
import { HardwareAssetsScopeMode } from "./../../../shared/models/ui/hardware-assets-scope-mode.model";

@Component({
	selector: "globalictcc-whatif-hardware-assets",
	standalone: true,
	imports: [
		ChipsComponent,
		ButtonComponent,
		TableComponent,
		UploadFileComponent,
		CheckboxComponent,
		ChartComponent,
		DropdownComponent,
		ReactiveFormsModule,
		NgClass,
		SpinnerComponent
	],
	templateUrl: "./whatif-hardware-assets.component.html",
	styleUrl: "./whatif-hardware-assets.component.scss"
})
export class WhatifHardwareAssetsComponent implements OnInit, OnDestroy {
	@ViewChild("newScenarioDialogContent") public newScenarioDialogContent: TemplateRef<unknown>;
	@ViewChild("uploadDeviceList") public uploadDeviceListDialogContent: TemplateRef<unknown>;

	private _selectedRows: Record<string, string> = {};
	private _isAddScenarioConfirmActionDisabled = signal(true);
	private _isUploadConfirmActionDisabled = signal(true);

	public isLoading: boolean = false;
	public hasError: boolean = false;
	public isErrorLogOpened: boolean = false;
	public errorLogMessage: string = "Something went wrong";
	public typeOptions: SelectOption[] = EMISSION_TYPE_OPTIONS;
	public typeControl = new FormControl(EMISSION_TYPE_OPTIONS[0].value);
	public orderByOptions: SelectOption[] = ORDER_BY_OPTIONS;
	public orderByControl = new FormControl(ORDER_BY_OPTIONS[0].value);
	public scenariosChips: ChipOption[] = [];
	public totalEmissionByManufactureChips: ChipOption[] = [
		{
			label: "PERCENTAGE VIEW",
			value: "PERCENTAGE",
			selected: true
		},
		{
			label: "TRUE VALUE VIEW",
			value: "VALUE",
			selected: false
		}
	];

	private _selectedMode = this.totalEmissionByManufactureChips[0]
		.value as HardwareAssetsScopeMode;

	public sameNameErrorMessage = signal<string>("");
	public uploadedFile: File;
	public uploadDeviceCheckboxControl = new FormControl(false);

	public emissionOverviewBarChart?: ChartResponse<BarChart>;
	public isEmissionOverviewBarChartLoading = false;
	public totalEmissionByCategoryBarChart?: ChartResponse<BarChart>;
	public isTotalEmissionByCategoryBarChartLoading = false;
	public totalEmissionByManufacturerBarChart?: ChartResponse<BarChart>;
	public isTotalEmissionByManufacturerBarChartLoading = false;

	public totalEmissionByCategoryBarChartColors: string[] = [];
	public totalEmissionByCategoryBarChartOverrides = CARBON_FOOTPRINT_BAR_CHART_OVERRIDES;
	public totalEmissionByManufacturerBarChartOverrides: EChartsOption = {
		series: { stack: "bar" }
	};

	public gridOptions: GridOptions = {
		columnDefs: [
			{ headerName: "Hardware list", field: "name" },
			{ headerName: "", field: "uploaded", cellRenderer: UploadedFileStatusComponent }
		],
		rowSelection: { mode: "multiRow" },
		rowData: [],
		onGridReady: this._onGridReady.bind(this),
		onRowSelected: this._onRowSelected.bind(this)
	};

	private _predefinedColors = [
		COLORS.GREEN_50,
		COLORS.YELLOW_100,
		COLORS.TURQUOISE_100,
		COLORS.ORANGE_50,
		COLORS.TURQUOISE_50,
		COLORS.GREY_50
	];
	private _tenantId: string = "";
	private _scenarioId: string = "";
	private _gridApi: GridApi<HardwareAssetsFile>;
	public isServiceNowEnabled: boolean = false;

	constructor(
		private _dialogService: DialogService,
		private _dashboardHardwareAssetsService: DashboardHardwareAssetsService,
		private _storageService: StorageService
	) {}

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();
		this.isServiceNowEnabled = this._storageService.getServiceNowEnabled();
	}

	ngOnDestroy() {
		this._deleteScenario();
	}

	/**
	 * Handles the AG Grid's grid ready event.
	 * Stores the grid API instance for later use and triggers the retrieval of files.
	 *
	 * @param params - The event parameters containing the grid API.
	 */
	private _onGridReady(params: GridReadyEvent) {
		this._gridApi = params.api;

		this.getFiles();
	}

	/**
	 * Handles the row selection event in the hardware assets table.
	 * Updates the internal map of selected rows with their IDs and names,
	 * and enables or disables the "Add Scenario" confirmation action based on selection.
	 *
	 * @param params - The row selection event containing the API to access selected rows.
	 */
	private _onRowSelected(params: RowSelectedEvent) {
		const selectedRows: HardwareAssetsFile[] = params.api.getSelectedRows();

		this._selectedRows = selectedRows.reduce(
			(acc, row) => {
				acc[row.id] = row.name;
				return acc;
			},
			{} as Record<string, string>
		);

		this._isAddScenarioConfirmActionDisabled.set(!selectedRows.length);
	}

	/**
	 * Fetches emission-related data by combining multiple observables.
	 *
	 * This method retrieves the emission overview, total emissions by category,
	 * and total emissions by manufacturer in parallel using `forkJoin`.
	 *
	 * @returns An Observable that emits an object containing:
	 * - `emissionOverview`: The emission overview data.
	 * - `totalEmissionByCategory`: The total emissions grouped by category.
	 * - `totalEmissionByManufacturer`: The total emissions grouped by manufacturer.
	 */
	public _fetchData() {
		const emissionOverview$ = this._getEmissionOverview();
		const totalEmissionByCategory$ = this._getTotalEmissionByCategory();
		const totalEmissionByManufacturer$ = this._getTotalEmissionByManufacturer();

		return forkJoin({
			emissionOverview: emissionOverview$,
			totalEmissionByCategory: totalEmissionByCategory$,
			totalEmissionByManufacturer: totalEmissionByManufacturer$
		});
	}

	/**
	 * Opens a dialog for uploading a hardware device list.
	 *
	 * The dialog displays a title, content, and a confirm action button.
	 * The confirm action is bound to the `_onConfirmUploadHardwareList` method.
	 * The confirm button can be disabled based on the `_isUploadConfirmActionDisabled` property.
	 *
	 * @remarks
	 * This method uses the `_dialogService` to present a modal dialog to the user,
	 * allowing them to upload a list of hardware devices.
	 */
	public onUploadHardwareList() {
		this._dialogService.open({
			data: {
				title: "Upload Device List",
				type: "confirm",
				content: this.uploadDeviceListDialogContent,
				confirmAction: this._onConfirmUploadHardwareList.bind(this),
				isConfirmActionDisabled: this._isUploadConfirmActionDisabled
			}
		});
	}

	/**
	 * Handles the confirmation of uploading a hardware list file.
	 *
	 * - Determines if the upload is temporary based on the checkbox control.
	 * - Prepares the file data and initiates the upload via the dashboard hardware assets service.
	 * - On successful upload, adds the uploaded file to the grid's row data and synchronizes selected rows.
	 *
	 * @private
	 */
	private _onConfirmUploadHardwareList() {
		const isTemporary = !this.uploadDeviceCheckboxControl.value;

		const fileFormData = new FormData();
		fileFormData.append("file", this.uploadedFile);

		this._dashboardHardwareAssetsService
			.uploadFile(this._tenantId, fileFormData, isTemporary)
			.pipe(
				tap({
					next: ({ fileId }) => {
						const files = this._gridApi.getGridOption("rowData") || [];
						const uploadedFile = {
							id: fileId,
							name: this.uploadedFile.name,
							uploaded: true
						};

						files.unshift(uploadedFile);

						this._gridApi.setGridOption("rowData", files);
						this._syncSelectedRows();
					}
				})
			)
			.subscribe();
	}

	/**
	 * Opens a dialog for adding a new scenario.
	 *
	 * This method uses the dialog service to display a confirmation dialog
	 * with a custom title, content, and confirm action. The confirm action
	 * is bound to the `_onConfirmAddScenario` method, and the confirm button
	 * can be conditionally disabled based on the `_isAddScenarioConfirmActionDisabled` flag.
	 *
	 * @remarks
	 * The dialog content is provided by `this.newScenarioDialogContent`.
	 * The dialog type is set to "confirm".
	 */
	public onAddNewScenario() {
		this._dialogService.open({
			data: {
				title: "Add New Scenario",
				type: "confirm",
				content: this.newScenarioDialogContent,
				confirmAction: this._onConfirmAddScenario.bind(this),
				isConfirmActionDisabled: this._isAddScenarioConfirmActionDisabled
			}
		});
	}

	/**
	 * Handles the confirmation action for adding a new scenario.
	 *
	 * This method sets the loading state, processes the selected rows to extract file identifiers,
	 * updates the scenario chips for display, synchronizes the selected rows, and triggers
	 * the retrieval of scenario data based on the selected files.
	 *
	 * @private
	 */
	private _onConfirmAddScenario() {
		this.isLoading = true;

		const files = Object.keys(this._selectedRows);

		this.scenariosChips = Object.entries(this._selectedRows).map(([id, name]) => ({
			value: id,
			label: name
		}));

		this._syncSelectedRows();
		this._getScenario(files);
	}

	/**
	 * Handles the removal of scenario chips.
	 *
	 * Updates the `scenariosChips` array with the provided chips, synchronizes the `_selectedRows`
	 * object to reflect the current selection, and triggers scenario data retrieval for the remaining files.
	 *
	 * @param chips - The updated array of `ChipOption` objects after removal.
	 */
	public onScenarioRemove(chips: ChipOption[]) {
		this.scenariosChips = chips.map(chip => ({
			...chip
		}));

		this._selectedRows = chips.reduce(
			(acc, chip) => {
				acc[chip.value] = chip.label;
				return acc;
			},
			{} as Record<string, string>
		);

		this._syncSelectedRows();

		const files = Object.keys(this._selectedRows);

		this._getScenario(files);
	}

	/**
	 * Handles changes to the order by selection for hardware assets.
	 * Updates the form control with the new order by value and triggers
	 * a refresh of the total emissions data grouped by manufacturer.
	 *
	 * @param orderBy - The new order by value(s) selected by the user.
	 */
	public onOrderByChange(orderBy: string | string[]) {
		this.orderByControl.setValue(orderBy as HardwareAssetsScopeOrderBy);

		this.getTotalEmissionByManufacturer();
	}

	/**
	 * Handles the file upload event by storing the uploaded file and updating the upload confirmation action state.
	 *
	 * @param file - The file object selected by the user for upload.
	 */
	public onFileUpload(file: File) {
		this.uploadedFile = file;
		this._isUploadConfirmActionDisabled.set(!file);
	}

	/**
	 * Synchronizes the selection state of grid rows with the internal `_selectedRows` map.
	 * Iterates over each node in the grid and sets its selection state based on whether
	 * its associated data's `id` exists in the `_selectedRows` object.
	 *
	 * @private
	 */
	private _syncSelectedRows() {
		this._gridApi.forEachNode(row => {
			row.setSelected(!!(row.data && this._selectedRows[row.data.id]));
		});
	}

	/**
	 * Handles the change of the hardware assets scope mode.
	 * Updates the selected mode and triggers the retrieval of total emissions by manufacturer.
	 *
	 * @param mode - The new mode selected, either as a string or number.
	 */
	public onModeChange(mode: string | number) {
		this._selectedMode = mode as HardwareAssetsScopeMode;

		this.getTotalEmissionByManufacturer();
	}

	/**
	 * Retrieves the list of hardware asset files for the current tenant and updates the grid with the results.
	 *
	 * This method calls the `_dashboardHardwareAssetsService.getFiles` method with the current tenant ID,
	 * transforms the response to extract file information, and updates the grid's row data with the list of files.
	 * Each file object in the resulting array contains the file's `id`, `name`, and an `uploaded` flag set to `false`.
	 * After updating the grid, it synchronizes the selected rows.
	 */
	public getFiles() {
		// if (this._storageService.getServiceNowEnabled()) {
		// 	this._gridApi?.setGridOption("rowData", []);
		// 	this._syncSelectedRows();
		// 	return;
		// }

		this._dashboardHardwareAssetsService
			.getFiles(this._tenantId)
			.pipe(
				map(({ files }) =>
					(files || []).map(file => ({
						id: file.id,
						name: file.name,
						uploaded: false
					}))
				),
				tap({
					next: files => {
						this._gridApi.setGridOption("rowData", files);
						this._syncSelectedRows();
					}
				})
			)
			.subscribe();
	}

	/**
	 * Retrieves the emission overview data for the current scenario and tenant,
	 * based on the selected emission view mode. Updates the emission overview bar chart
	 * and loading state accordingly. Handles errors by setting an error state on the chart.
	 *
	 * @returns An Observable that emits the emission overview bar chart data.
	 */
	private _getEmissionOverview() {
		this.isEmissionOverviewBarChartLoading = true;

		const viewMode = (this.typeControl.value || "Total emissions") as EmissionViewMode;

		return this._dashboardHardwareAssetsService
			.getEmissionOverviewForBarChart(this._scenarioId, this._tenantId, viewMode)
			.pipe(
				tap({
					next: chartResponse => (this.emissionOverviewBarChart = chartResponse),
					error: () => {
						this.emissionOverviewBarChart = {
							chart: {} as BarChart,
							hasError: true
						};
					},
					finalize: () => (this.isEmissionOverviewBarChartLoading = false)
				})
			);
	}

	/**
	 * Initiates the retrieval of the emission overview data by subscribing to the observable
	 * returned by the private `_getEmissionOverview` method.
	 *
	 * @remarks
	 * This method triggers the emission overview data fetch process. The subscription is not stored,
	 * so it is intended for fire-and-forget usage. Ensure proper handling of subscriptions to avoid
	 * potential memory leaks.
	 */
	public getEmissionOverview() {
		this._getEmissionOverview().subscribe();
	}

	/**
	 * Retrieves the total emission data grouped by category and updates the bar chart state accordingly.
	 *
	 * This method sets the loading state, invokes the dashboard hardware assets service to fetch
	 * emission data for the current scenario and tenant, and processes the response to update the
	 * chart data and colors. If an error occurs, it sets an error state for the chart. The loading
	 * state is reset when the operation completes.
	 *
	 * @returns An observable that emits the bar chart response containing emission data by category.
	 *
	 * @private
	 */
	private _getTotalEmissionByCategory() {
		this.isTotalEmissionByCategoryBarChartLoading = true;

		return this._dashboardHardwareAssetsService
			.getTotalEmissionByCategory(this._scenarioId, this._tenantId)
			.pipe(
				tap({
					next: chartResponse => {
						this.totalEmissionByCategoryBarChart = chartResponse;

						this.totalEmissionByCategoryBarChartColors =
							chartResponse.chart.barSeries.reduce((acc, _, index) => {
								const color =
									index < this._predefinedColors.length
										? this._predefinedColors[index]
										: COLORS.GREY_50;

								acc.push(color);

								return acc;
							}, [] as string[]);
					},
					error: () => {
						this.totalEmissionByCategoryBarChart = {
							chart: {} as BarChart,
							hasError: true
						};
					},
					finalize: () => (this.isTotalEmissionByCategoryBarChartLoading = false)
				})
			);
	}

	/**
	 * Initiates the retrieval of total emissions grouped by category.
	 *
	 * This method calls the private `_getTotalEmissionByCategory` method and subscribes to its observable,
	 * triggering the emission calculation process. The results are handled internally.
	 */
	public getTotalEmissionByCategory() {
		this._getTotalEmissionByCategory().subscribe();
	}

	/**
	 * Retrieves and updates the total emission data by manufacturer for the bar chart.
	 *
	 * This method sets the loading state, determines the selected mode and order,
	 * and calls the dashboard hardware assets service to fetch emission data.
	 * On success, it updates the bar chart data; on error, it sets an error state.
	 * The loading state is reset when the observable completes.
	 *
	 * @returns An Observable that emits the chart response for total emissions by manufacturer.
	 */
	private _getTotalEmissionByManufacturer() {
		this.isTotalEmissionByManufacturerBarChartLoading = true;

		const mode = this._selectedMode as HardwareAssetsScopeMode;
		const orderBy = (this.orderByControl.value ||
			ORDER_BY_OPTIONS[0].value) as HardwareAssetsScopeOrderBy;

		return this._dashboardHardwareAssetsService
			.getTotalEmissionByManufacturer(this._scenarioId, this._tenantId, mode, orderBy)
			.pipe(
				tap({
					next: chartResponse =>
						(this.totalEmissionByManufacturerBarChart = chartResponse),
					error: () => {
						this.totalEmissionByManufacturerBarChart = {
							chart: {} as BarChart,
							hasError: true
						};
					},
					finalize: () => (this.isTotalEmissionByManufacturerBarChartLoading = false)
				})
			);
	}

	/**
	 * Initiates the process to retrieve and handle the total emissions data grouped by manufacturer.
	 *
	 * This method calls a private method `_getTotalEmissionByManufacturer` which returns an observable,
	 * and subscribes to it to trigger the data retrieval and any associated side effects.
	 *
	 * @remarks
	 * The actual emission data processing and subscription handling are managed within the private method.
	 */
	public getTotalEmissionByManufacturer() {
		this._getTotalEmissionByManufacturer().subscribe();
	}

	/**
	 * Initiates the scenario creation process by uploading the provided files and polling for the scenario status.
	 *
	 * This method performs the following steps:
	 * 1. Sets the loading state to true.
	 * 2. Calls the dashboard hardware assets service to create a new scenario with the given files.
	 * 3. Stores the returned scenario ID.
	 * 4. Polls the scenario status every 2 seconds until the status is either "DONE" or "ERROR".
	 * 5. If an error status is received, sets the error state and logs the error message.
	 * 6. If successful, disables the loading state and fetches the scenario data.
	 *
	 * @param files - An array of file names to be uploaded for scenario creation.
	 * @returns An Observable that completes when the scenario is ready or errors if the process fails.
	 */
	private _getScenario(files: string[]) {
		this.isLoading = true;

		this._dashboardHardwareAssetsService
			.getScenario(files)
			.pipe(
				tap({ next: ({ scenarioId }) => (this._scenarioId = scenarioId) }),
				switchMap(() =>
					this._dashboardHardwareAssetsService
						.getScenarioStatus(this._scenarioId, this._tenantId)
						.pipe(
							repeat({ delay: 2000 }),
							takeWhile(
								({ status }) => status !== "DONE" && status !== "ERROR",
								true
							),
							last()
						)
				),
				switchMap(({ status, message }) => {
					this.isLoading = false;

					if (status === "ERROR") {
						this.hasError = true;

						if (message) this.errorLogMessage = message;

						return throwError(
							() =>
								new ApplicationError(
									"Error while getting uploading status",
									this.constructor.name,
									this._onConfirmAddScenario.name,
									false
								)
						);
					}

					return this._fetchData();
				})
			)
			.subscribe();
	}

	/**
	 * Deletes the current scenario using the scenario ID and tenant ID.
	 * If the scenario ID is not set, the method returns immediately.
	 * Calls the dashboard hardware assets service to perform the deletion.
	 * The deletion request is executed as an observable subscription.
	 *
	 * @private
	 */
	private _deleteScenario() {
		if (!this._scenarioId) return;

		this._dashboardHardwareAssetsService
			.deleteScenario(this._scenarioId, this._tenantId)
			.subscribe();
	}
}
